package com.castly.admin.ui.analytics

import androidx.compose.runtime.*
import com.castly.admin.api.ApiClient
import com.castly.admin.ui.components.StatCard
import com.castly.admin.ui.icons.*
import com.castly.admin.ui.theme.*
import com.castly.shared.dto.*
import kotlinx.coroutines.async
import kotlinx.coroutines.coroutineScope
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

@Composable
fun AnalyticsPage(apiClient: ApiClient) {
    val colors = AppTheme.colors

    var overview by remember { mutableStateOf<AnalyticsOverviewDto?>(null) }
    var trends by remember { mutableStateOf<AnalyticsTrendsDto?>(null) }
    var topContent by remember { mutableStateOf<AnalyticsTopContentDto?>(null) }
    var engagement by remember { mutableStateOf<AnalyticsEngagementDto?>(null) }
    var retention by remember { mutableStateOf<AnalyticsRetentionDto?>(null) }
    var heatmap by remember { mutableStateOf<AnalyticsHeatmapDto?>(null) }
    var health by remember { mutableStateOf<AnalyticsHealthDto?>(null) }
    var loading by remember { mutableStateOf(true) }
    var error by remember { mutableStateOf<String?>(null) }

    LaunchedEffect(Unit) {
        try {
            coroutineScope {
                val o = async { apiClient.getAnalyticsOverview() }
                val t = async { apiClient.getAnalyticsTrends() }
                val tc = async { apiClient.getAnalyticsTopContent() }
                val e = async { apiClient.getAnalyticsEngagement() }
                val r = async { apiClient.getAnalyticsRetention() }
                val hm = async { apiClient.getAnalyticsHeatmap() }
                val hl = async { apiClient.getAnalyticsHealth() }
                overview = o.await()
                trends = t.await()
                topContent = tc.await()
                engagement = e.await()
                retention = r.await()
                heatmap = hm.await()
                health = hl.await()
            }
        } catch (e: Exception) {
            error = e.message ?: "Failed to load analytics"
        } finally {
            loading = false
        }
    }

    Div({
        style {
            maxWidth(1200.px)
        }
    }) {
        H2({
            style {
                margin(0.px)
                marginBottom(Spacing.lg)
                fontSize(Typography.xxl)
                color(Color(colors.text))
                fontWeight("700")
            }
        }) { Text("Analytics") }

        if (loading) {
            Div({
                style {
                    padding(Spacing.xxl)
                    textAlign("center")
                    color(Color(colors.textMuted))
                }
            }) { Text("Loading analytics...") }
            return@Div
        }

        error?.let { err ->
            Div({
                style {
                    errorMessageStyle(colors)
                }
            }) { Text(err) }
            return@Div
        }

        OverviewSection(overview, loading)
        TrendsSection(trends)
        EngagementSection(engagement)
        TopContentSection(topContent)
        RetentionSection(retention)
        HeatmapSection(heatmap)
        HealthSection(health)
    }
}

@Composable
private fun SectionTitle(text: String) {
    val colors = AppTheme.colors
    H3({
        style {
            margin(0.px)
            marginTop(Spacing.xxl)
            marginBottom(Spacing.lg)
            fontSize(Typography.xl)
            color(Color(colors.text))
            fontWeight("600")
            paddingBottom(Spacing.sm)
            property("border-bottom", "1px solid ${colors.border}")
        }
    }) { Text(text) }
}

@Composable
private fun OverviewSection(overview: AnalyticsOverviewDto?, loading: Boolean) {
    SectionTitle("Overview")
    Div({
        style {
            display(DisplayStyle.Grid)
            property("grid-template-columns", "repeat(auto-fill, minmax(200px, 1fr))")
            gap(Spacing.lg)
        }
    }) {
        StatCard(
            title = "Total Likes",
            value = if (loading) "..." else "${overview?.totalLikes ?: 0}",
            icon = { IconCheck(size = 24) },
            isLoading = loading
        )
        StatCard(
            title = "Total Streams",
            value = if (loading) "..." else "${overview?.totalStreams ?: 0}",
            icon = { IconHeadphones(size = 24) },
            isLoading = loading
        )
        StatCard(
            title = "Unique Listeners",
            value = if (loading) "..." else "${overview?.uniqueListeners ?: 0}",
            icon = { IconUsers(size = 24) },
            isLoading = loading
        )
        StatCard(
            title = "Listening Hours",
            value = if (loading) "..." else formatDecimal(overview?.totalListeningHours ?: 0.0),
            icon = { IconClock(size = 24) },
            isLoading = loading
        )
        StatCard(
            title = "Avg Completion",
            value = if (loading) "..." else "${formatPercent(overview?.avgCompletionRate ?: 0.0)}%",
            icon = { IconBarChart(size = 24) },
            isLoading = loading
        )
        StatCard(
            title = "Avg Min/Episode",
            value = if (loading) "..." else formatDecimal(overview?.avgMinutesPerEpisode ?: 0.0),
            icon = { IconClock(size = 24) },
            isLoading = loading
        )
        StatCard(
            title = "Likes (30d)",
            value = if (loading) "..." else "${overview?.likes30d ?: 0}",
            icon = { IconCheck(size = 24) },
            isLoading = loading
        )
        StatCard(
            title = "Streams (30d)",
            value = if (loading) "..." else "${overview?.streams30d ?: 0}",
            icon = { IconHeadphones(size = 24) },
            isLoading = loading
        )
    }
}

@Composable
private fun TrendsSection(trends: AnalyticsTrendsDto?) {
    val colors = AppTheme.colors
    SectionTitle("30-Day Trends")

    Div({
        style {
            display(DisplayStyle.Grid)
            property("grid-template-columns", "1fr 1fr")
            gap(Spacing.lg)
            marginBottom(Spacing.lg)
        }
    }) {
        SimpleBarChart(
            data = trends?.dailyStreams ?: emptyList(),
            title = "Daily Streams"
        )
        SimpleBarChart(
            data = trends?.dailyLikes ?: emptyList(),
            title = "Daily Likes",
            barColor = colors.success
        )
    }

    if (trends != null && trends.contentTypeBreakdown.isNotEmpty()) {
        Div({
            style {
                display(DisplayStyle.Grid)
                property("grid-template-columns", "repeat(auto-fill, minmax(200px, 1fr))")
                gap(Spacing.lg)
            }
        }) {
            trends.contentTypeBreakdown.forEach { (type, count) ->
                StatCard(
                    title = "${type.replaceFirstChar { it.uppercase() }} Streams",
                    value = "$count",
                    icon = { if (type == "audiobook") IconBookOpen(size = 24) else IconHeadphones(size = 24) }
                )
            }
        }
    }
}

@Composable
private fun EngagementSection(engagement: AnalyticsEngagementDto?) {
    val colors = AppTheme.colors
    if (engagement == null) return

    SectionTitle("Engagement Quality")

    Div({
        style {
            display(DisplayStyle.Grid)
            property("grid-template-columns", "repeat(auto-fill, minmax(200px, 1fr))")
            gap(Spacing.lg)
            marginBottom(Spacing.lg)
        }
    }) {
        StatCard(
            title = "Avg Min (All)",
            value = formatDecimal(engagement.avgMinutesAll),
            icon = { IconClock(size = 24) }
        )
        StatCard(
            title = "Avg Min (Audiobook)",
            value = formatDecimal(engagement.avgMinutesAudiobook),
            icon = { IconBookOpen(size = 24) }
        )
        StatCard(
            title = "Avg Min (Podcast)",
            value = formatDecimal(engagement.avgMinutesPodcast),
            icon = { IconHeadphones(size = 24) }
        )
    }

    if (engagement.audiobookEngagement.isNotEmpty()) {
        Div({
            style { cardStyle(colors); marginBottom(Spacing.lg) }
        }) {
            H4({
                style {
                    margin(0.px); marginBottom(Spacing.md)
                    fontSize(Typography.lg); color(Color(colors.text)); fontWeight("600")
                }
            }) { Text("Audiobook Engagement (Top 20)") }

            DataTable(
                headers = listOf("Title", "Streams", "Avg Min", "Completion", "Skip Rate"),
                rows = engagement.audiobookEngagement.map { ab ->
                    listOf(
                        ab.title,
                        "${ab.totalStreams}",
                        formatDecimal(ab.avgMinutesListened),
                        "${formatPercent(ab.avgCompletionRate)}%",
                        "${formatPercent(ab.skipRate)}%"
                    )
                }
            )
        }
    }

    if (engagement.mostSkippedEpisodes.isNotEmpty()) {
        Div({
            style { cardStyle(colors) }
        }) {
            H4({
                style {
                    margin(0.px); marginBottom(Spacing.md)
                    fontSize(Typography.lg); color(Color(colors.text)); fontWeight("600")
                }
            }) { Text("Most Skipped Episodes") }

            DataTable(
                headers = listOf("Episode", "Content", "Streams", "Avg Min", "Avg %", "Skips"),
                rows = engagement.mostSkippedEpisodes.map { ep ->
                    listOf(
                        ep.episodeTitle,
                        ep.parentTitle,
                        "${ep.totalStreams}",
                        formatDecimal(ep.avgMinutesReached),
                        "${formatPercent(ep.avgCompletionPct / 100.0)}%",
                        "${ep.skipCount}"
                    )
                }
            )
        }
    }
}

@Composable
private fun TopContentSection(topContent: AnalyticsTopContentDto?) {
    val colors = AppTheme.colors
    if (topContent == null) return

    SectionTitle("Top Content")

    var activeTab by remember { mutableStateOf(0) }
    val tabs = listOf(
        "Most Loved" to topContent.topByLikes,
        "Most Streamed" to topContent.topByStreams,
        "Most Completed" to topContent.topByCompletions,
        "Most Skipped" to topContent.mostSkipped,
        "Over-hyped" to topContent.overHyped,
        "Under-discovered" to topContent.underDiscovered
    )

    Div({
        style { cardStyle(colors) }
    }) {
        // Tab bar
        Div({
            style {
                display(DisplayStyle.Flex)
                gap(0.px)
                marginBottom(Spacing.lg)
                property("border-bottom", "1px solid ${colors.border}")
                property("overflow-x", "auto")
            }
        }) {
            tabs.forEachIndexed { index, (label, _) ->
                Button({
                    onClick { activeTab = index }
                    style {
                        tabButtonStyle(colors, activeTab == index)
                    }
                }) { Text(label) }
            }
        }

        val (_, items) = tabs[activeTab]
        if (items.isEmpty()) {
            Div({
                style {
                    padding(Spacing.lg)
                    textAlign("center")
                    color(Color(colors.textMuted))
                    fontSize(Typography.sm)
                }
            }) { Text("No data") }
        } else {
            DataTable(
                headers = listOf("#", "Title", "Type", "Count"),
                rows = items.mapIndexed { i, item ->
                    listOf(
                        "${i + 1}",
                        item.title,
                        item.contentType,
                        "${item.metricValue}"
                    )
                }
            )
        }
    }
}

@Composable
private fun RetentionSection(retention: AnalyticsRetentionDto?) {
    val colors = AppTheme.colors
    if (retention == null) return

    SectionTitle("Listener Retention")

    if (retention.cohorts.isNotEmpty()) {
        Div({
            style { cardStyle(colors); marginBottom(Spacing.lg) }
        }) {
            H4({
                style {
                    margin(0.px); marginBottom(Spacing.md)
                    fontSize(Typography.lg); color(Color(colors.text)); fontWeight("600")
                }
            }) { Text("Weekly Cohort Retention") }

            Div({ style { property("overflow-x", "auto") } }) {
                Table({
                    style {
                        width(100.percent)
                        property("border-collapse", "collapse")
                        fontSize(Typography.sm)
                    }
                }) {
                    Thead {
                        Tr {
                            Th({ style { tableHeaderStyle(colors) } }) { Text("Cohort") }
                            Th({ style { tableHeaderStyle(colors) } }) { Text("Users") }
                            val maxWeeks = retention.cohorts.maxOfOrNull { it.retainedByWeek.size } ?: 0
                            (1..maxWeeks).forEach { w ->
                                Th({ style { tableHeaderStyle(colors) } }) { Text("W+$w") }
                            }
                        }
                    }
                    Tbody {
                        retention.cohorts.forEach { cohort ->
                            Tr {
                                Td({ style { cellStyle(colors) } }) { Text(cohort.cohortWeek) }
                                Td({ style { cellStyle(colors); fontWeight("600") } }) { Text("${cohort.cohortSize}") }
                                cohort.retainedByWeek.forEach { retained ->
                                    val pct = if (cohort.cohortSize > 0) (retained.toDouble() / cohort.cohortSize * 100).toInt() else 0
                                    Td({
                                        style {
                                            cellStyle(colors)
                                            backgroundColor(Color(retentionCellColor(colors, pct)))
                                        }
                                    }) { Text("$pct%") }
                                }
                            }
                        }
                    }
                }
            }
        }
    }

    if (retention.newVsReturning.isNotEmpty()) {
        SimpleBarChart(
            data = retention.newVsReturning.map { TimeSeriesPoint(it.date, (it.newUsers + it.returningUsers).toLong()) },
            title = "Daily Active Listeners (New + Returning)"
        )
    }
}

@Composable
private fun HeatmapSection(heatmap: AnalyticsHeatmapDto?) {
    if (heatmap == null) return
    SectionTitle("Listening Heatmap")
    HeatmapChart(
        cells = heatmap.cells,
        title = "Streams by Day & Hour"
    )
}

@Composable
private fun HealthSection(health: AnalyticsHealthDto?) {
    val colors = AppTheme.colors
    if (health == null) return

    SectionTitle("Content Health")

    Div({
        style {
            display(DisplayStyle.Grid)
            property("grid-template-columns", "repeat(auto-fill, minmax(200px, 1fr))")
            gap(Spacing.lg)
            marginBottom(Spacing.lg)
        }
    }) {
        StatCard(
            title = "Draft Content",
            value = "${health.draftContentCount}",
            icon = { IconFileText(size = 24) }
        )
        StatCard(
            title = "Zero Streams",
            value = "${health.zeroStreamContent.size}",
            icon = { IconAlertCircle(size = 24) }
        )
        StatCard(
            title = "No Episodes",
            value = "${health.audiobooksNoEpisodes.size}",
            icon = { IconAlertCircle(size = 24) }
        )
        StatCard(
            title = "Zero Duration",
            value = "${health.episodesZeroDuration.size}",
            icon = { IconAlertCircle(size = 24) }
        )
    }

    if (health.zeroStreamContent.isNotEmpty()) {
        HealthTable("Content with Zero Streams", health.zeroStreamContent)
    }
    if (health.audiobooksNoEpisodes.isNotEmpty()) {
        HealthTable("Audiobooks with No Episodes", health.audiobooksNoEpisodes)
    }
    if (health.episodesZeroDuration.isNotEmpty()) {
        HealthTable("Episodes with Zero Duration", health.episodesZeroDuration)
    }
}

@Composable
private fun HealthTable(title: String, items: List<ContentHealthItem>) {
    val colors = AppTheme.colors
    Div({
        style { cardStyle(colors); marginBottom(Spacing.lg) }
    }) {
        H4({
            style {
                margin(0.px); marginBottom(Spacing.md)
                fontSize(Typography.lg); color(Color(colors.text)); fontWeight("600")
            }
        }) { Text(title) }

        DataTable(
            headers = listOf("Title", "Type", "Created"),
            rows = items.map { item ->
                listOf(item.title, item.contentType, item.createdAt.take(10))
            }
        )
    }
}

@Composable
private fun DataTable(headers: List<String>, rows: List<List<String>>) {
    val colors = AppTheme.colors
    Div({ style { property("overflow-x", "auto") } }) {
        Table({
            style {
                width(100.percent)
                property("border-collapse", "collapse")
                fontSize(Typography.sm)
            }
        }) {
            Thead {
                Tr {
                    headers.forEach { h ->
                        Th({ style { tableHeaderStyle(colors) } }) { Text(h) }
                    }
                }
            }
            Tbody {
                rows.forEach { row ->
                    Tr {
                        row.forEach { cell ->
                            Td({ style { cellStyle(colors) } }) { Text(cell) }
                        }
                    }
                }
            }
        }
    }
}

private fun StyleScope.cellStyle(colors: AppColors) {
    padding(Spacing.sm, Spacing.md)
    property("border-bottom", "1px solid ${colors.border}")
    color(Color(colors.text))
    themeTransition()
}

private fun retentionCellColor(colors: AppColors, pct: Int): String {
    if (pct == 0) return colors.surfaceHover
    val r = colors.success.drop(1).take(2).toInt(16)
    val g = colors.success.drop(3).take(2).toInt(16)
    val b = colors.success.drop(5).take(2).toInt(16)
    val alpha = (pct.toDouble() / 100.0 * 0.6).coerceIn(0.05, 0.6)
    return "rgba($r, $g, $b, $alpha)"
}

private fun formatDecimal(value: Double): String {
    val rounded = (value * 10).toLong() / 10.0
    return if (rounded == rounded.toLong().toDouble()) "${rounded.toLong()}" else "$rounded"
}

private fun formatPercent(value: Double): String {
    val pct = (value * 1000).toLong() / 10.0
    return if (pct == pct.toLong().toDouble()) "${pct.toLong()}" else "$pct"
}
